import React, { useEffect, useMemo } from 'react'

// components
import { ButtonComponent } from '@/components/commons/buttons/ButtonComponent'
import { ModalPage } from '@/components/ui/modals/ModalPage.component'
import { EditIconPen, Icon } from 'public/assets/v2/images/icons'

import { ImageEditor } from '@/components/commons/imageEditor'
import { useProcessImage } from '@/components/commons/imageEditor/hook/ProccesImage'
import { imageModalEditor } from './styles/index.css'
import { ImageFit } from '../images'

interface ImageEditorModalProps {
  title?: string
  subtitle?: string
  primaryButtonlabel?: string
  showModal?: boolean
  width?: number
  imageContainerWidth?: number | string
  height?: number
  borderRadius?: number
  originalImage?: string
  aspectRatio?: string
  maxWidth?: number | string
  closeModal?: () => void
  onSaveImage?: (url: string) => void
  serviceUpload: (file: File) => Promise<string>
}

export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  title = 'Preview image',
  subtitle = '',
  primaryButtonlabel = 'Update image',
  showModal = false,
  width = 250,
  height = 250,
  borderRadius = 20,
  originalImage,
  closeModal,
  onSaveImage,
  aspectRatio = '1/1',
  maxWidth = '100%',
  imageContainerWidth = 250,
  serviceUpload,
}) => {
  const fileInput = React.useRef(null)

  const { loading, setNewFile, getImageEdited } = useProcessImage(serviceUpload)

  const [imageEditorData, setImageEditorData] = React.useState({
    cropperOpen: false,
    img: null,
    zoom: 2,
    croppedImg: originalImage || null,
  })

  useEffect(() => {
    setImageEditorData({
      cropperOpen: false,
      img: null,
      zoom: 2,
      croppedImg: originalImage || null,
    })
  }, [originalImage, showModal])

  const processImage = async () => {
    const res = await getImageEdited()
    if (res) {
      onSaveImage?.(res)
      setImageEditorData({
        ...imageEditorData,
        cropperOpen: false,
        croppedImg: res,
      })
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = URL.createObjectURL(e.target.files[0])
    setImageEditorData({ ...imageEditorData, img: url, cropperOpen: true })
  }

  const buttonLabel = imageEditorData.cropperOpen ? 'Save' : primaryButtonlabel

  const icon = imageEditorData.cropperOpen ? (
    <Icon.CheckmarkIcon className="text-16" />
  ) : (
    <EditIconPen className="text-16" />
  )

  const buttonAction = imageEditorData.cropperOpen
    ? processImage
    : () => fileInput.current.click()
  const primaryButtonVariation = imageEditorData.cropperOpen
    ? 'primary'
    : 'secondary'

  const handleCancel = () => {
    setImageEditorData({
      ...imageEditorData,
      img: null,
      cropperOpen: false,
      croppedImg: originalImage,
    })
  }

  const randomeKey = useMemo(() => {
    return Math.random().toString(36).substring(7)
  }, [])

  return (
    <ModalPage
      show={showModal}
      classNameContainer={[imageModalEditor]}
      onClose={closeModal}
      className={[imageModalEditor]}
    >
      <div className="text-center p-4">
        <h6 className="text-center text-primary font-medium m-0 text-20 text-24--tablet">
          {title}
        </h6>
        <h2 className="text-center text-tertirary font-medium m-0 text-16 text-14--tablet mt-10">
          {subtitle}
        </h2>
        <div className="full-w mx-auto py-24 py-32--tablet flex items-center justify-center">
          <div className="relative flex flex-column items-center full-w">
            {imageEditorData.cropperOpen ? (
              <ImageEditor
                onFileChange={setNewFile}
                width={width}
                height={height}
                image={imageEditorData.img}
              />
            ) : (
              <div
                style={{
                  aspectRatio,
                  maxWidth,
                  margin: '0 auto',
                  width: imageContainerWidth,
                }}
                className="relative mt-2"
              >
                <ImageFit
                  onClick={() => fileInput.current.click()}
                  src={imageEditorData.croppedImg || originalImage}
                  key={
                    (imageEditorData.croppedImg || originalImage) +
                    '_' +
                    randomeKey
                  }
                  alt={'image_to_edit'}
                  style={{
                    borderRadius,
                    overflow: 'hidden',
                    objectFit: 'cover',
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="mt-60 mt-0--tablet flex flex-column gap-12">
          <ButtonComponent
            fullWidth
            variation={primaryButtonVariation}
            size="regular"
            label={buttonLabel}
            iconLeft={icon}
            onClick={buttonAction}
            loading={loading}
          />
          {imageEditorData.cropperOpen && (
            <ButtonComponent
              fullWidth
              variation="secondary"
              size="regular"
              label={'Cancel'}
              iconLeft={<Icon.CloseIcon className="text-16" />}
              onClick={handleCancel}
              loading={loading}
            />
          )}
        </div>
      </div>
      <input
        className="hidden"
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
      />
    </ModalPage>
  )
}
